import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    IS_BLOCKED,
    IS_CHOSEN_PUBLIC_FILE,
    IS_FROZEN,
    IS_MY_TEAM,
    IS_PUBLIC_FOLDER,
    PUBLIC_NOT_AVAILABLE,
    PUBLIC_NOT_EXISTS,
} from 'reactApp/appHelpers/configHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage, isPageWithoutHeader, isSubscriptionPage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EHeaderColor, HeaderWrapper } from 'reactApp/ui/Header/HeaderWrapper';
import { LeftSide } from 'reactApp/ui/Header/LeftSide';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { RightSide } from 'reactApp/ui/Header/RightSide';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { ViewerShareButton } from 'reactApp/ui/ViewerShareButton/ViewerShareButton';

export const Header = memo(() => {
    const storage = useSelector(getCurrentStorage);
    const { isHome, isSearch, isAttaches, isGallery, isTrashBin } = getStorage(storage);

    const queryParams = useSelector(SettingsSelectors.getQueryParams);
    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    const hideHeader =
        isPageWithoutHeader() ||
        (isPhone && (isSubscriptionPage() || isHome || IS_PUBLIC_FOLDER || isHome || isGallery || isTrashBin)) ||
        IS_MY_TEAM;

    const searchOnlyExpanded = isSearch || (isAttaches && Boolean(queryParams?.query));
    const [isSearchExpanded, setSearchExpanded] = useState<boolean>(searchOnlyExpanded);

    const toggleSearch = useCallback((toggleState: boolean) => {
        setSearchExpanded(toggleState);
    }, []);

    useEffect(() => {
        if (!searchOnlyExpanded) {
            setSearchExpanded(false);
        }
    }, [searchOnlyExpanded, storage]);

    const content = useMemo(() => {
        if (IS_FROZEN || IS_BLOCKED || PUBLIC_NOT_EXISTS || PUBLIC_NOT_AVAILABLE) {
            return <Logo />;
        }

        if (isPhone && IS_CHOSEN_PUBLIC_FILE) {
            return (
                <>
                    <Logo />
                    <ViewerShareButton basic placement={tooltipPlacements.BOTTOM_RIGHT} />
                </>
            );
        }

        return (
            <>
                <LeftSide storage={storage} toggleSearch={toggleSearch} isSearchExpanded={isSearchExpanded} />
                <RightSide
                    storage={storage}
                    toggleSearch={toggleSearch}
                    isSearchExpanded={isSearchExpanded}
                    searchOnlyExpanded={searchOnlyExpanded}
                />
            </>
        );
    }, [isPhone, isSearchExpanded, searchOnlyExpanded, storage, toggleSearch]);

    if (hideHeader) {
        return null;
    }

    return (
        <HeaderWrapper color={isPhone ? EHeaderColor.BLUE : EHeaderColor.WHITE} isPhone={isPhone}>
            {content}
        </HeaderWrapper>
    );
});

Header.displayName = 'Header';
